// What Monaco should know about the code a scraper scenario holds.
//
// x-scraper runs that code as the BODY of an async arrow function:
//
//     async ({ page, browser, input, proxy, setText, setImage, setResult, log,
//              Buffer, setTimeout, clearTimeout, setInterval, clearInterval }) => { … }
//
// so top-level `await` and `return` are valid, and those names are in scope.
// Monaco sees a plain script instead, so it is told about the names (an ambient
// declaration file) and told not to flag the two statements a script cannot
// have. The timer functions need no declaring: the DOM library Monaco loads for
// JavaScript already has them, and declaring them again would be a duplicate.

const SANDBOX_DTS = `
/** The Puppeteer Page the run drives. */
declare const page: any;
/** The Puppeteer Browser behind \`page\`. */
declare const browser: any;
/**
 * The run's input: the scenario's template, overlaid by the settings action's
 * stored input (credentials), overlaid by the keys core adds per call — or, in a
 * preview, by the run input.
 */
declare const input: { [key: string]: any };
/** The proxy URL the job goes out through, from the settings row's proxy list, if any. */
declare const proxy: string | undefined;
/** Finish with a text result. */
declare function setText(text: string): void;
/** Finish with an image result. */
declare function setImage(data: any, extension?: string): void;
/** Finish with a result: { type: 'text' | 'image', data, extension }. */
declare function setResult(result: { type?: 'text' | 'image'; data: any; extension?: string }): void;
/** Write a line to the run's log (the preview's Log tab). */
declare function log(...args: any[]): void;
/** Node's Buffer. */
declare const Buffer: any;
`;

// 1108: a 'return' statement can only be used within a function body.
// 1375: 'await' expressions are only allowed at the top level of a module.
// 1378: top-level 'await' needs the 'module' option set to es2022 or later.
const IGNORED_DIAGNOSTICS = [1108, 1375, 1378];

let installed = false;

/**
 * beforeMount for the scenario's code editor. Monaco is one instance for the
 * whole page and these settings are global to its JavaScript service, so they
 * are applied once.
 */
export function installScenarioSandbox(monaco) {
    if (installed) return;
    const javascript = monaco.languages?.typescript?.javascriptDefaults;
    if (!javascript) return;

    javascript.addExtraLib(SANDBOX_DTS, 'ts:scraper-scenario-sandbox.d.ts');
    javascript.setDiagnosticsOptions({
        noSemanticValidation: false,
        noSyntaxValidation: false,
        diagnosticCodesToIgnore: IGNORED_DIAGNOSTICS
    });
    installed = true;
}
